import { type FC } from "react";
import { Button, toast } from "@capacit-com/lara-law-shared-ui";
import { Download, Loader2, UserCog } from "lucide-react";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { useUserManagementContext } from "@/providers/user-management/UserManagementContext";
import useUserManagementExport from "../hooks/useUserManagementExport";
import {
  BULK_EDIT_PERMISSION,
  EXPORT_PERMISSION,
  UserManagementTabs,
} from "../utils/userManagementTabs";

interface UserManagementTableActionsProps {
  tab: UserManagementTabs;
}

/**
 * The actions that operate on a sub-tab's table — bulk edit and export — rendered at the right
 * end of every tab's search row. The order is fixed so "Eksportér til Excel" is the right-most
 * control on all four tabs, and "Masseredigering" sits directly left of it wherever a tab has one.
 */
const UserManagementTableActions: FC<UserManagementTableActionsProps> = ({
  tab,
}) => {
  const {
    bulkMode,
    setBulkMode,
    tenant,
    existingUsersSearch,
    existingUsersSortBy,
    existingUsersSortAscending,
    invitedUsersSortBy,
    invitedUsersSortAscending,
    groupsSearch,
    groupsSortBy,
    groupsSortAscending,
    orphanedWorkspacesSearch,
    orphanedWorkspacesSortBy,
    orphanedWorkspacesSortAscending,
  } = useUserManagementContext();
  const {
    exportUsers,
    exportInvitedUsers,
    exportGroups,
    exportOrphanedWorkspaces,
    isExporting,
  } = useUserManagementExport();

  const bulkEditPermission = BULK_EDIT_PERMISSION[tab];

  /** Exports whatever the tab is showing, across every page. */
  const exportTab = (): Promise<number> => {
    const tenantName = tenant?.name;

    switch (tab) {
      case UserManagementTabs.INVITED_USERS:
        return exportInvitedUsers({
          sortBy: invitedUsersSortBy,
          sortAscending: invitedUsersSortAscending,
          tenantName,
        });
      case UserManagementTabs.GROUPS:
        return exportGroups({
          search: groupsSearch || undefined,
          sortBy: groupsSortBy,
          sortAscending: groupsSortAscending,
          tenantName,
        });
      case UserManagementTabs.ORPHANED_WORKSPACES:
        return exportOrphanedWorkspaces({
          search: orphanedWorkspacesSearch || undefined,
          sortBy: orphanedWorkspacesSortBy,
          sortAscending: orphanedWorkspacesSortAscending,
          tenantName,
        });
      default:
        return exportUsers({
          search: existingUsersSearch || undefined,
          sortBy: existingUsersSortBy,
          sortAscending: existingUsersSortAscending,
          tenantName,
        });
    }
  };

  // Failures are logged inside useUserManagementExport; this only tells the user.
  const handleExport = async () => {
    try {
      const exportedRows = await exportTab();
      toast.success(
        `${exportedRows} ${exportedRows === 1 ? "række" : "rækker"} eksporteret`
      );
    } catch {
      toast.error("Fejl ved eksport. Prøv igen.");
    }
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      {bulkEditPermission && (
        <PermissionGate allOf={[bulkEditPermission]}>
          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            onClick={() => setBulkMode((prev) => !prev)}
          >
            <UserCog className="w-4 h-4 mr-2" />
            Masseredigering
          </Button>
        </PermissionGate>
      )}
      <PermissionGate allOf={[EXPORT_PERMISSION[tab]]}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isExporting}
          title="Hent alle rækker fra denne fane som et Excel-regneark"
        >
          {isExporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {isExporting ? "Eksporterer..." : "Eksportér til Excel"}
        </Button>
      </PermissionGate>
    </div>
  );
};

export default UserManagementTableActions;
